import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import arrow from '../../assets/images/arrow-right-black.svg';
import useOnClickOutside from '../hooks/useOnClickOutside';
import CheckBoxSection from './CheckBoxSection';
import SearchSection from './SearchSection';
import FooterSection from './FooterSection';

import './MultiSelectSearch.scss';

/**
 * MultiSelectSearch
 *
 * @param {Object} props
 * @param {String} props.label - Label name for the button
 * @param {Object[]} props.options - Array of objects with id, label and value
 * @param {String} props.options.label - Label name for the checkbox
 * @param {String} props.options.value - Value for the checkbox
 * @param {String} props.options.checked - Checked for the checkbox selection

 * @param {Function} props.onApply - Callback function to apply the selected options
 *
* @param {String} props.className - class name to ovewrite stlyes
* @param {String} props.showLongSelectionText - show text selection or only number

 * @returns {JSX.Element}
 */

const MultiSelectSearch = ({
	label = '',
	options = [],
	onApply = () => {},
	className,
	showLongSelectionText,
	disabled = false
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [filterOptions, setFilterOptions] = useState(options);
	const [persistOptions, setPersistOptions] = useState(options);
	const [selectAllOption, setSelectAllOption] = useState(false);
	const [search, setSearch] = useState('');

	const containerRef = useRef(null);

	const syncOptions = (_options, _filterOptions) =>
		_options.map((option) => {
			const index = _filterOptions.findIndex((filterOption) => filterOption.value === option.value);
			if (index !== -1) {
				return { ...option, checked: _filterOptions[index].checked };
			}
			return option;
		});

	const compareOptions = (_options, _filterOptions) =>
		Object.keys(_options).length === Object.keys(_filterOptions).length &&
		Object.keys(_options).every((p) => _options[p] === _filterOptions[p]);

	const handleCheckboxChange = (value) => {
		const index = filterOptions.findIndex((option) => option.value === value);
		const newFilterOption = { ...filterOptions[index], checked: !filterOptions[index].checked };
		const newFilterOptions = [...filterOptions];
		newFilterOptions[index] = newFilterOption;
		setFilterOptions(newFilterOptions);
		setPersistOptions(syncOptions(persistOptions, newFilterOptions));
	};

	const handleSelectAll = () => {
		const newFilterOption = filterOptions.map((option) => ({ ...option, checked: !selectAllOption }));
		setPersistOptions(syncOptions(persistOptions, newFilterOption));
		setFilterOptions(newFilterOption);
		setSelectAllOption(!selectAllOption);
	};

	const handleSearch = useCallback(
		(_search) => {
			const newFilterOptions = persistOptions.filter((option) =>
				option.label.toLowerCase().includes(_search.toLowerCase())
			);
			setFilterOptions(newFilterOptions);
		},
		[persistOptions]
	);

	const handleApply = () => {
		setSearch('');
		setIsOpen(false);
		onApply(persistOptions);
	};

	useEffect(() => {
		if (filterOptions) {
			const allChecked = filterOptions.every((option) => option.checked);
			setSelectAllOption(allChecked);
		}
	}, [filterOptions]);

	useEffect(() => {
		if (
			filterOptions.length !== persistOptions.length ||
			!filterOptions.every((element, index) => element === persistOptions[index])
		) {
			setFilterOptions(persistOptions);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [persistOptions]);

	useEffect(() => {
		if (handleSearch) {
			handleSearch(search);
		}
	}, [search, handleSearch]);

	useEffect(() => {
		if (
			options.length !== persistOptions.length ||
			!persistOptions.every((element, index) => element === options[index])
		) {
			setPersistOptions(options);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [options]);

	const handleCancel = () => {
		setIsOpen(false);
		setSearch('');
		setPersistOptions(options);
	};

	useOnClickOutside(containerRef, handleCancel, 'mousedown');

	const isDisabledApply =
		persistOptions.every((option) => option.checked === false) || compareOptions(persistOptions, options);

	const optionsSelected = options.filter((option) => option.checked);

	const noResults = search && !filterOptions.length;

	return (
		<div className={classNames('multi-select-container', className)} ref={containerRef}>
			<button
				className={classNames('button', { active: isOpen, disabled: !options?.length })}
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				disabled={disabled}
			>
				<span>
					{label}
					{optionsSelected.length < options.length &&
						(showLongSelectionText ? (
							<>
								{' '}
								<b>({optionsSelected.length})</b>
							</>
						) : (
							<>
								{': '}
								<b>{`${optionsSelected.length} selected`}</b>
							</>
						))}
				</span>
				<img src={arrow} alt="Toggle dropdown" className="dropdown-arrow" />
			</button>
			{isOpen && (
				<div className="content">
					<div className={classNames('search-section', { 'no-results-search': noResults })}>
						<SearchSection search={search} setSearch={setSearch} noResults={noResults} />
					</div>
					<div className="checkbox-section">
						{!!filterOptions.length && !noResults && (
							<CheckBoxSection
								selectAllOption={selectAllOption}
								onSelectAll={handleSelectAll}
								checkboxOptions={filterOptions}
								onCheckboxChange={handleCheckboxChange}
							/>
						)}
					</div>
					<hr />
					<div className="footer-section">
						<FooterSection onCancel={handleCancel} onApply={handleApply} isDisabled={isDisabledApply} />
					</div>
				</div>
			)}
		</div>
	);
};

export default MultiSelectSearch;
